import { PropsWithChildren, useMemo } from "react";
import {
  type HTMLMotionProps,
  MotionConfig,
  type Transition,
  type Variants,
  useReducedMotion,
} from "motion/react";
import { useUIStore } from "@stores/useUIStore";

const softEase = [0.22, 1, 0.36, 1] as const;

type RevealItemOptions = {
  compact?: boolean;
  layout?: boolean;
};

type RevealItemProps = Pick<
  HTMLMotionProps<"div">,
  "animate" | "custom" | "exit" | "initial" | "layout" | "variants"
>;

export const motionTiming = {
  fast: { duration: 0.16, ease: softEase },
  standard: { duration: 0.24, ease: softEase },
  slow: { duration: 0.36, ease: softEase },
  spring: { type: "spring", stiffness: 420, damping: 34, mass: 0.8 },
} satisfies Record<string, Transition>;

export const listContainerVariants: Variants = {
  hidden: { opacity: 1 },
  show: {
    opacity: 1,
    transition: {
      staggerChildren: 0.045,
      delayChildren: 0.035,
    },
  },
  exit: {
    opacity: 0,
    transition: motionTiming.fast,
  },
};

export const listItemVariants: Variants = {
  hidden: {
    opacity: 0,
    y: 10,
    scale: 0.985,
    filter: "blur(3px)",
  },
  show: {
    opacity: 1,
    y: 0,
    scale: 1,
    filter: "blur(0px)",
    transition: motionTiming.standard,
  },
  exit: {
    opacity: 0,
    y: -6,
    scale: 0.985,
    filter: "blur(2px)",
    transition: motionTiming.fast,
  },
};

export const compactItemVariants: Variants = {
  hidden: {
    opacity: 0,
    y: 6,
  },
  show: {
    opacity: 1,
    y: 0,
    transition: motionTiming.fast,
  },
  exit: {
    opacity: 0,
    y: -4,
    transition: motionTiming.fast,
  },
};

export const revealContainerVariants: Variants = {
  hidden: { opacity: 1 },
  show: { opacity: 1 },
  exit: {
    opacity: 0,
    transition: motionTiming.fast,
  },
};

export const revealItemVariants: Variants = {
  hidden: {
    opacity: 0,
    y: 14,
    scale: 0.985,
    filter: "blur(3px)",
  },
  show: (index = 0) => ({
    opacity: 1,
    y: 0,
    scale: 1,
    filter: "blur(0px)",
    transition: {
      ...motionTiming.standard,
      delay: index * 0.045,
    },
  }),
  exit: {
    opacity: 0,
    y: -6,
    scale: 0.985,
    filter: "blur(2px)",
    transition: motionTiming.fast,
  },
};

export const revealCompactItemVariants: Variants = {
  hidden: {
    opacity: 0,
    y: 8,
  },
  show: (index = 0) => ({
    opacity: 1,
    y: 0,
    transition: {
      ...motionTiming.fast,
      delay: index * 0.035,
    },
  }),
  exit: {
    opacity: 0,
    y: -4,
    transition: motionTiming.fast,
  },
};

const reducedContainerVariants: Variants = {
  hidden: { opacity: 1 },
  show: { opacity: 1 },
  exit: { opacity: 1 },
};

const reducedItemVariants: Variants = {
  hidden: { opacity: 0 },
  show: { opacity: 1, transition: { duration: 0.01 } },
  exit: { opacity: 0, transition: { duration: 0.01 } },
};

export function useDashboardMotion() {
  const animationReduced = useUIStore((state) => state.animationReduced);
  const systemReducedMotion = useReducedMotion();
  const shouldReduceMotion = Boolean(animationReduced || systemReducedMotion);

  return useMemo(
    () => {
      const revealContainer = shouldReduceMotion
        ? reducedContainerVariants
        : revealContainerVariants;
      const revealItem = shouldReduceMotion
        ? reducedItemVariants
        : revealItemVariants;
      const revealCompactItem = shouldReduceMotion
        ? reducedItemVariants
        : revealCompactItemVariants;
      const revealItemProps = (
        index = 0,
        options: RevealItemOptions = {},
      ): RevealItemProps => ({
        custom: index,
        layout: options.layout ?? !shouldReduceMotion,
        variants: options.compact ? revealCompactItem : revealItem,
        initial: "hidden",
        animate: "show",
        exit: "exit",
      });
      const revealCompactItemProps = (
        index = 0,
        options: Omit<RevealItemOptions, "compact"> = {},
      ) => revealItemProps(index, { ...options, compact: true });
      const revealStaticItemProps = (
        index = 0,
        options: Omit<RevealItemOptions, "layout"> = {},
      ) => revealItemProps(index, { ...options, layout: false });
      const revealStaticCompactItemProps = (index = 0) =>
        revealItemProps(index, { compact: true, layout: false });

      return {
        shouldReduceMotion,
        container: shouldReduceMotion
          ? reducedContainerVariants
          : listContainerVariants,
        item: shouldReduceMotion ? reducedItemVariants : listItemVariants,
        compactItem: shouldReduceMotion ? reducedItemVariants : compactItemVariants,
        revealListProps: {
          variants: revealContainer,
          initial: "hidden",
          animate: "show",
        },
        revealItemProps,
        revealCompactItemProps,
        revealStaticItemProps,
        revealStaticCompactItemProps,
        hoverLift: shouldReduceMotion ? undefined : { y: -3 },
        press: shouldReduceMotion ? undefined : { y: 1, scale: 0.995 },
      };
    },
    [shouldReduceMotion],
  );
}

export function DashboardMotionProvider({ children }: PropsWithChildren) {
  const animationReduced = useUIStore((state) => state.animationReduced);

  return (
    <MotionConfig
      reducedMotion={animationReduced ? "always" : "user"}
      transition={motionTiming.standard}
    >
      {children}
    </MotionConfig>
  );
}
